import React, { useMemo } from "react";
import { Icon } from "semantic-ui-react";

import styles from "./UnitsSoldChart.module.scss";
import { formatMonthTitle } from "./UnitsSoldChart.form";

const chart = {
  width: 700,
  height: 230,
  paddingTop: 22,
  paddingRight: 20,
  paddingBottom: 34,
  paddingLeft: 40,
};

function formatNumber(value) {
  return (Number(value) || 0).toLocaleString("es-GT");
}

export function UnitsSoldChart({ data = [], isLoading = false }) {
  const chartData = useMemo(() => {
    const values = data.map((item) => Number(item.unitsSold) || 0);
    const maxValue = Math.max(...values, 1);

    const graphWidth = chart.width - chart.paddingLeft - chart.paddingRight;
    const graphHeight = chart.height - chart.paddingTop - chart.paddingBottom;
    const slot = graphWidth / Math.max(data.length - 1, 1);

    function getX(index) {
      return data.length === 1
        ? chart.paddingLeft + graphWidth / 2
        : chart.paddingLeft + slot * index;
    }

    function getY(value) {
      return (
        chart.paddingTop + graphHeight - (value / maxValue) * graphHeight
      );
    }

    const baselineY = chart.paddingTop + graphHeight;

    const points = data.map((item, index) => {
      const value = Number(item.unitsSold) || 0;

      return { ...item, x: getX(index), y: getY(value), value };
    });

    const linePath = points
      .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
      .join(" ");

    const areaPath =
      points.length > 0
        ? `${linePath} L ${points[points.length - 1].x} ${baselineY} L ${points[0].x} ${baselineY} Z`
        : "";

    const yLabels = Array.from({ length: 4 }, (_, index) => {
      const value = (maxValue / 3) * (3 - index);
      const y = chart.paddingTop + (graphHeight / 3) * index;

      return { value, y };
    });

    return { points, linePath, areaPath, yLabels };
  }, [data]);

  const totalUnits = data.reduce(
    (total, item) => total + (Number(item.unitsSold) || 0),
    0
  );

  const hasAnySales = data.some((item) => Number(item.unitsSold) > 0);

  return (
    <section className={styles.UnitsSoldChart}>
      <header className={styles.ChartHeader}>
        <div>
          <h2>Artículos vendidos</h2>
          <p>Piezas vendidas por mes según las salidas de inventario.</p>
        </div>

        <div className={styles.TotalBadge}>
          <span className={styles.SummaryLabel}>Total del período</span>
          <strong>{formatNumber(totalUnits)}</strong>
        </div>
      </header>

      {isLoading ? (
        <div className={styles.EmptyState}>
          <span>Cargando...</span>
        </div>
      ) : !hasAnySales ? (
        <div className={styles.EmptyState}>
          <Icon name="chart line" />
          <span>Todavía no hay salidas registradas.</span>
        </div>
      ) : (
        <div className={styles.ChartWrapper}>
          <svg
            viewBox={`0 0 ${chart.width} ${chart.height}`}
            className={styles.ChartSvg}
            role="img"
            aria-label="Gráfica de artículos vendidos por mes"
          >
            <defs>
              <linearGradient id="unitsAreaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8f2350" stopOpacity="0.16" />
                <stop offset="100%" stopColor="#8f2350" stopOpacity="0" />
              </linearGradient>
            </defs>

            {chartData.yLabels.map((item) => (
              <g key={item.y}>
                <line
                  x1={chart.paddingLeft}
                  x2={chart.width - chart.paddingRight}
                  y1={item.y}
                  y2={item.y}
                  className={styles.GridLine}
                />

                <text
                  x={chart.paddingLeft - 9}
                  y={item.y + 4}
                  textAnchor="end"
                  className={styles.YAxisLabel}
                >
                  {formatNumber(Math.round(item.value))}
                </text>
              </g>
            ))}

            <path d={chartData.areaPath} className={styles.AreaFill} />
            <path d={chartData.linePath} className={styles.Line} />

            {chartData.points.map((point) => (
              <g key={point.key}>
                <title>
                  {formatMonthTitle(point.year, point.month)}: {point.value}{" "}
                  {point.value === 1 ? "pieza" : "piezas"}
                </title>

                {point.value > 0 && (
                  <text
                    x={point.x}
                    y={point.y - 11}
                    textAnchor="middle"
                    className={styles.ValueLabel}
                  >
                    {point.value}
                  </text>
                )}

                <circle
                  cx={point.x}
                  cy={point.y}
                  r={3.5}
                  className={styles.Point}
                />

                <text
                  x={point.x}
                  y={chart.height - 12}
                  textAnchor="middle"
                  className={styles.XAxisLabel}
                >
                  {point.label}
                </text>
              </g>
            ))}
          </svg>
        </div>
      )}
    </section>
  );
}
